import React, { createContext, useContext, useState, useEffect } from 'react';
import { modulesAPI } from '@/services';
import { useAuth } from './AuthContext';

const ModulesContext = createContext();

const DEBUG_MODULES =
  typeof process !== 'undefined' &&
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_DEBUG_MODULES === '1';

export const useModules = () => {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('useModules must be used within a ModulesProvider');
  }
  return context;
};

export const ModulesProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadModules = async () => {
    try {
      const response = await modulesAPI.getAll();
      const modulesData = response.data || [];
      if (DEBUG_MODULES) {
        console.log('[ModulesContext] Modules chargés:', modulesData.length, modulesData);
      }
      setModules(modulesData);
    } catch (err) {
      console.error('[ModulesContext] Erreur lors du chargement des modules:', err);
      // En cas d'erreur, utiliser un tableau vide (les routes seront accessibles par défaut)
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    const hasProjectContext = !!localStorage.getItem('project_id');
    if (!isAuthenticated || !hasProjectContext) {
      setModules([]);
      setLoading(false);
      return;
    }

    loadModules();
    
    // Rafraîchir les modules toutes les 60 secondes pour détecter les changements
    const interval = setInterval(() => {
      loadModules();
    }, 60000); // 60 secondes
    
    return () => clearInterval(interval);
  }, [isAuthenticated, authLoading]);

  const refreshModules = () => {
    loadModules();
  };

  // Vérifier si un module est activé
  const isModuleEnabled = (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    return module ? module.enabled : true; // Par défaut activé si non trouvé
  };

  // Vérifier si un composant est activé
  const isComponentEnabled = (moduleId, componentId) => {
    const module = modules.find(m => m.id === moduleId);
    if (!module || !module.enabled) {
      return false; // Si le module est désactivé, le composant l'est aussi
    }
    const component = module.components?.find(c => c.id === componentId);
    return component ? component.enabled : true; // Par défaut activé si non trouvé
  };

  // Vérifier si une route est accessible (vérifie le module et le composant)
  const isRouteEnabled = (route) => {
    // Si aucun module n'est chargé, permettre l'accès (évite les blocages au démarrage)
    if (modules.length === 0) {
      return true;
    }
    
    // Parcourir tous les modules et leurs composants
    for (const module of modules) {
      // Vérifier tous les composants, même si le module est désactivé
      // pour détecter si la route appartient à un module désactivé
      for (const component of module.components || []) {
        if (component.route === route) {
          // Si le module est désactivé, le composant est inaccessible
          if (!module.enabled) {
            if (DEBUG_MODULES) {
              console.log('[ModulesContext] Route désactivée (module désactivé):', route, 'Module:', module.name);
            }
            return false;
          }
          // Si le module est activé, vérifier l'état du composant
          const enabled = component.enabled;
          if (!enabled && DEBUG_MODULES) {
            console.log('[ModulesContext] Route désactivée (composant désactivé):', route, 'Composant:', component.name);
          }
          return enabled;
        }
      }
    }
    
    // Route absente du catalogue modules (ex. /, /meeting-minutes) : accessible par défaut
    return true;
  };

  // Obtenir le module associé à une route
  const getModuleByRoute = (route) => {
    for (const module of modules) {
      if (!module.enabled) continue;
      
      for (const component of module.components || []) {
        if (component.route === route) {
          return { module, component };
        }
      }
    }
    return null;
  };

  return (
    <ModulesContext.Provider value={{ 
      modules, 
      loading, 
      refreshModules,
      isModuleEnabled,
      isComponentEnabled,
      isRouteEnabled,
      getModuleByRoute
    }}>
      {children}
    </ModulesContext.Provider>
  );
};

